import { ApiService } from '../api/api.service';
import { initGoogleMaps } from '@/helpers';
import { Config } from '@/environment';

import { Store } from 'vuex';
import { AppState } from '@/store';
import { AxiosInstance } from 'axios';
import { Logger } from '../logger';
import { AnalyticsService } from '../analytics/analytics.service';

export interface GoogleMapsService {
  getGoogleMaps(): Promise<typeof google>;
  googlePlacesAutoComplete(input: string, placeType: string): Promise<QueryAutocompletePrediction[]>;
  googleGeocodeSearchString(input: string): Promise<google.maps.LatLng>;
  getPlaceDetails(placeId: string, map: google.maps.Map): Promise<google.maps.places.PlaceResult>;
  getDirectionsUrl(lat?: number, lng?: number): string;
}

export class AppGoogleMapsService extends ApiService implements GoogleMapsService {
  private autocompleteService!: google.maps.places.AutocompleteService;
  private placesService!: google.maps.places.PlacesService;
  private geocoder!: google.maps.Geocoder;
  private sessionToken!: google.maps.places.AutocompleteSessionToken;

  constructor(private config: Config, protected store: Store<AppState>, protected httpClient: AxiosInstance, protected logger: Logger, protected analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  getGoogleMaps() {
    return initGoogleMaps(this.config.google);
  }

  googlePlacesAutoComplete(input: string, placeType: string) {
    if (!this.sessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }
    
    const localContext = this;

    return new Promise(async (resolve: (value: QueryAutocompletePrediction[]) => void, reject: (value: google.maps.places.PlacesServiceStatus) => void) => {
      const displaySuggestions = (predictions: QueryAutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) => {
        if (status != google.maps.places.PlacesServiceStatus.OK) {
          if (status != google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            localContext.logger.error(`Something went wrong while executing googlePlacesAutoComplete`, { status });
          }
          reject(status);
        }

        resolve(predictions);
      };

      if (!this.autocompleteService) {
        this.autocompleteService = new google.maps.places.AutocompleteService();
      }

      this.autocompleteService.getPlacePredictions(
        {
          input,
          types: [placeType],
          // United States, United Kingdom, Canada, France and Germany
          componentRestrictions: { country: ['us', 'gb', 'ca', 'fr', 'de'] },
          sessionToken: this.sessionToken,
        },
        displaySuggestions,
      );
    });
  }

  getPlaceDetails(placeId: string, map: google.maps.Map): Promise<google.maps.places.PlaceResult> {
    if (!this.placesService) {
      this.placesService = new google.maps.places.PlacesService(map);
    }
    if (!this.sessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }

    return new Promise(async (resolve, reject) => {
      this.placesService.getDetails(
        {
          placeId,
          sessionToken: this.sessionToken,
          fields: [
            'address_components',
            'geometry'
          ]
        },
        (results: google.maps.places.PlaceResult, status: google.maps.places.PlacesServiceStatus) => {
          this.sessionToken = new google.maps.places.AutocompleteSessionToken();
          if (status == google.maps.places.PlacesServiceStatus.OK) {
            resolve(results);
          } else {
            reject(status);
          }
        },
      );
    });
  }

  // use for city state and specific addresses
  googleGeocodeSearchString(input: string): Promise<google.maps.LatLng> {
    return new Promise(async (resolve, reject) => {
      if (!this.geocoder) {
        this.geocoder = new google.maps.Geocoder();
      }

      this.geocoder.geocode({ address: input }, (results, status) => {
        if (status == google.maps.GeocoderStatus.OK) {
          resolve(results[0].geometry.location);
        } else if (status == google.maps.GeocoderStatus.ZERO_RESULTS) {
          resolve(undefined);
        } else {
          reject(status);
        }
      });
    });
  }

  getDirectionsUrl(lat?: number, lng?: number): string {
    return `http://maps.google.com/maps?f=d&source=s_d&daddr=${lat},${lng}`;
  }
}
